import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Link from '@mui/material/Link';
import type { ConsentDocumentKind, ConsentFormState } from '@/types/consent';
import { termsCopy } from '@/Pages/Terms/copy';
import '@/Pages/Terms/TermsPage.sass';

type TermsCheckboxesProps = {
  form: ConsentFormState;
  onAcceptedTermsChange: (value: boolean) => void;
  onAcceptedPrivacyChange: (value: boolean) => void;
  onAcceptedMarketingChange: (value: boolean) => void;
  onOpenDocument: (document: ConsentDocumentKind, triggerElement: HTMLElement) => void;
};

type DocumentLinkProps = {
  document: ConsentDocumentKind;
  label: string;
  testId: string;
  onOpenDocument: (document: ConsentDocumentKind, triggerElement: HTMLElement) => void;
};

function DocumentLink({
  document,
  label,
  testId,
  onOpenDocument,
}: DocumentLinkProps): JSX.Element {
  return (
    <Link
      component="button"
      type="button"
      className="terms-checkboxes__link"
      data-testid={testId}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenDocument(document, event.currentTarget);
      }}
    >
      {label}
    </Link>
  );
}

export function TermsCheckboxes({
  form,
  onAcceptedTermsChange,
  onAcceptedPrivacyChange,
  onAcceptedMarketingChange,
  onOpenDocument,
}: TermsCheckboxesProps): JSX.Element {
  return (
    <FormGroup className="terms-checkboxes" data-testid="terms-checkboxes">
      <FormControlLabel
        control={
          <Checkbox
            checked={form.acceptedTerms}
            onChange={(event) => onAcceptedTermsChange(event.target.checked)}
            data-testid="terms-checkbox-terms"
          />
        }
        label={
          <Box component="span">
            {termsCopy.checkboxTermsPrefix}{' '}
            <DocumentLink
              document="terms"
              label={termsCopy.checkboxTermsLink}
              testId="terms-link-terms"
              onOpenDocument={onOpenDocument}
            />
          </Box>
        }
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={form.acceptedPrivacy}
            onChange={(event) => onAcceptedPrivacyChange(event.target.checked)}
            data-testid="terms-checkbox-privacy"
          />
        }
        label={
          <Box component="span">
            {termsCopy.checkboxPrivacyPrefix}{' '}
            <DocumentLink
              document="privacy"
              label={termsCopy.checkboxPrivacyLink}
              testId="terms-link-privacy"
              onOpenDocument={onOpenDocument}
            />
          </Box>
        }
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={form.acceptedMarketing}
            onChange={(event) => onAcceptedMarketingChange(event.target.checked)}
            data-testid="terms-checkbox-marketing"
          />
        }
        label={termsCopy.checkboxMarketing}
      />
    </FormGroup>
  );
}
